import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Compass, MapPin, Wrench, Star, X, Lightning, Users } from "@phosphor-icons/react";

// Module-level in-flight cache keyed by job.id — dedupes React.StrictMode's
// double-mount so exactly ONE POST reaches the backend per modal open.
const _pendingSuggest = new Map();

/**
 * AutoDispatchSuggestModal — admin can preview the top-scoring workers for
 * an unassigned job. When settings.auto_dispatch_mode === 'auto' the modal
 * shows an "Assign #1 Automatically" button. In 'suggest' mode each candidate
 * has a manual Assign button.
 */
export default function AutoDispatchSuggestModal({ job, mode = "suggest", onClose, onAssigned }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const key = job.id;
    let p = _pendingSuggest.get(key);
    if (!p) {
      p = api.post(`/jobs/${key}/auto-dispatch-suggest`, { apply: false })
        .finally(() => _pendingSuggest.delete(key));
      _pendingSuggest.set(key, p);
    }
    p.then(r => { if (!cancelled) setResult(r.data); })
     .catch(e => {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suggestion failed");
          onClose?.();
        }
      })
     .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [job.id]);

  const assign = async (workerId, workerName) => {
    setAssigning(workerId);
    try {
      await api.patch(`/jobs/${job.id}`, { assigned_to: workerId });
      toast.success(`Assigned to ${workerName}`);
      onAssigned?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Assign failed");
    } finally { setAssigning(null); }
  };

  const autoAssign = async () => {
    setAssigning("__auto__");
    try {
      const r = await api.post(`/jobs/${job.id}/auto-dispatch-suggest`, { apply: true });
      if (r.data.applied) {
        toast.success(`Auto-assigned to ${r.data.applied.name} · score ${r.data.applied.score}`);
        onAssigned?.();
        onClose?.();
      } else {
        toast.error("No eligible worker to auto-assign");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-assign failed");
    } finally { setAssigning(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6"
        onClick={e => e.stopPropagation()} data-testid="auto-dispatch-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass weight="fill" size={22} className="text-fuchsia-600"/>
            <h3 className="font-display font-black text-xl uppercase tracking-tight">Auto-Dispatch</h3>
            {mode === "auto" && (
              <span className="px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-red-600 text-white">
                Auto-Assign ON
              </span>
            )}
          </div>
          <button onClick={onClose} data-testid="auto-dispatch-close"><X weight="bold" size={22}/></button>
        </div>

        {loading && <div className="mt-6 text-center font-black uppercase text-xs tracking-widest text-slate-500" data-testid="auto-dispatch-loading">Scoring workers…</div>}

        {result && (
          <>
            <div className="mt-3 flex items-center gap-2 flex-wrap text-[10px] font-black uppercase tracking-widest">
              {result.trade_needed && (
                <span className="px-1.5 py-0.5 bg-cyan-700 text-white inline-flex items-center gap-1" data-testid="auto-dispatch-trade">
                  <Wrench weight="fill" size={10}/> Trade: {result.trade_needed}
                </span>
              )}
              {result.severity === "urgent" && (
                <span className="px-1.5 py-0.5 bg-red-600 text-white inline-flex items-center gap-1">
                  <Lightning weight="fill" size={10}/> Urgent
                </span>
              )}
              {!result.job_coords && (
                <span className="text-amber-700">⚠ Job not geocoded — distance scoring unavailable</span>
              )}
            </div>

            {result.candidates?.length ? (
              <div className="mt-4 space-y-2" data-testid="auto-dispatch-candidates">
                {result.candidates.map((c, i) => {
                  const isTop = i === 0;
                  return (
                    <div key={c.worker_id}
                      data-testid={`auto-dispatch-candidate-${i}`}
                      className={`p-3 border-2 ${isTop ? "border-fuchsia-600 bg-fuchsia-50" : "border-slate-300 bg-white"}`}>
                      <div className="flex items-center gap-2">
                        {isTop && <Star weight="fill" size={16} className="text-fuchsia-600"/>}
                        <div className="font-display font-black text-lg leading-none">{c.name}</div>
                        <div className={`ml-auto px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${isTop ? "bg-fuchsia-600 text-white" : "bg-slate-700 text-white"}`}
                          data-testid={`auto-dispatch-score-${i}`}>
                          Score {c.score}
                        </div>
                      </div>
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {c.distance_mi != null && (
                          <span className="inline-flex items-center gap-0.5">
                            <MapPin size={10}/> {c.distance_mi} mi
                          </span>
                        )}
                        {c.open_jobs > 0 && <span>· {c.open_jobs} open</span>}
                        {c.skills?.length > 0 && <span>· {c.skills.slice(0,3).join(" / ")}</span>}
                      </div>
                      <div className="mt-1 text-[10px] text-slate-600">
                        {(c.reasons || []).join(" · ")}
                      </div>
                      <button onClick={() => assign(c.worker_id, c.name)} disabled={assigning === c.worker_id}
                        data-testid={`auto-dispatch-assign-${i}`}
                        className={`mt-2 w-full h-9 font-black uppercase text-[10px] tracking-widest border-2 border-[#0f172a] disabled:opacity-50 ${isTop ? "bg-fuchsia-600 hover:bg-fuchsia-700 text-white" : "bg-white hover:bg-slate-100 text-[#0f172a]"}`}>
                        {assigning === c.worker_id ? "Assigning…" : (isTop ? "Assign This Tech" : "Assign")}
                      </button>
                    </div>
                  );
                })}

                {mode === "auto" && (
                  <button onClick={autoAssign} disabled={assigning !== null}
                    data-testid="auto-dispatch-auto-assign"
                    className="mt-3 w-full h-11 bg-red-600 hover:bg-red-700 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a] disabled:opacity-50 inline-flex items-center justify-center gap-1">
                    <Lightning weight="fill" size={14}/> {assigning === "__auto__" ? "Dispatching…" : "Auto-Assign Top Match"}
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-6 text-center text-sm font-black uppercase tracking-widest text-slate-500 flex flex-col items-center gap-2">
                <Users size={24} className="text-slate-300"/>
                No eligible workers.
                <span className="text-[10px] normal-case tracking-normal">Give workers skills + a home ZIP in Team → Edit for better matches.</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
